import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  MessageSquare,
  PenSquare,
  Search,
} from 'lucide-react';

import { usePageContext } from '@/hooks/usePageContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/ui/StatCard';
import { listSurveys, type Survey } from '@/services/surveys.service';

const DASHBOARD_PAGE_SIZE = 5000;

function hasExcessiveHyphens(title: string): boolean {
  let count = 0;
  for (const ch of title) {
    if (ch === '-') {
      count++;
      if (count >= 4) return true;
    }
  }
  return false;
}

type DashboardSurvey = Survey & {
  createdAt?: string | null;
  assignmentCount?: number;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return 'No date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'No date';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function toTimestamp(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

async function listDashboardSurveys(): Promise<DashboardSurvey[]> {
  const result = await listSurveys({ isActive: true, page: 1, perPage: DASHBOARD_PAGE_SIZE });
  const all = result.data as DashboardSurvey[];
  return all.filter((s) => !hasExcessiveHyphens(s.title));
}

function SnapshotEmptyState({ message }: { message: string }) {
  return <p className="text-sm text-slate-500">{message}</p>;
}

export default function SurveysDashboardPage() {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Forms Dashboard');
  const [search, setSearch] = useState('');

  const surveysQuery = useQuery({
    queryKey: ['surveys-dashboard-all'],
    queryFn: () => listDashboardSurveys(),
    staleTime: 60_000,
  });

  const allSurveys = surveysQuery.data ?? [];

  const surveys = useMemo(() => {
    if (!search.trim()) return allSurveys;
    const q = search.toLowerCase();
    return allSurveys.filter((s) => s.title.toLowerCase().includes(q));
  }, [allSurveys, search]);

  const totalSurveys = surveys.length;
  const formsWithQuestions = surveys.filter((survey) => (survey.questionCount ?? 0) > 0).length;
  const formsWithResponses = surveys.filter((survey) => (survey.responseCount ?? 0) > 0).length;

  const newestForms = useMemo(
    () =>
      [...surveys]
        .sort(
          (a, b) =>
            toTimestamp(b.dateCreated ?? b.createdAt) - toTimestamp(a.dateCreated ?? a.createdAt) ||
            b.id - a.id,
        )
        .slice(0, 5),
    [surveys],
  );

  const topResponseForms = useMemo(
    () =>
      [...surveys]
        .filter((survey) => (survey.responseCount ?? 0) > 0)
        .sort(
          (a, b) =>
            (b.responseCount ?? 0) - (a.responseCount ?? 0) ||
            (b.questionCount ?? 0) - (a.questionCount ?? 0),
        )
        .slice(0, 5),
    [surveys],
  );

  const attentionForms = useMemo(
    () =>
      [...surveys]
        .filter((survey) => (survey.questionCount ?? 0) === 0)
        .sort((a, b) => (a.responseCount ?? 0) - (b.responseCount ?? 0))
        .slice(0, 6),
    [surveys],
  );

  useEffect(() => {
    updateAIContext({
      dataSummary: `Forms dashboard shows ${totalSurveys} active forms, ${formsWithQuestions} with questions, and ${formsWithResponses} with responses.`,
    });
  }, [formsWithQuestions, formsWithResponses, totalSurveys, updateAIContext]);

  if (surveysQuery.isLoading && !surveysQuery.data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" label="Loading forms..." />
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-y-auto p-6">
      <PageHeader
        title="Forms"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search forms..."
                className="h-9 w-56 rounded-md border border-slate-300 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <Button variant="secondary" onClick={() => navigate('/surveys/all')}>
              All Forms
            </Button>
            {topResponseForms[0] ? (
              <Button onClick={() => navigate(`/surveys/${topResponseForms[0].id}/responses`)}>
                Review Top Responses
              </Button>
            ) : null}
          </div>
        )}
      />

      <MetricCardGrid columns={3}>
        <StatCard
          label="Total Forms"
          value={totalSurveys}
          subtitle="Active forms in this account"
          tone="blue"
          icon={<ClipboardList className="h-4 w-4" />}
          onClick={() => navigate('/surveys/all')}
        />
        <StatCard
          label="With Questions"
          value={formsWithQuestions}
          subtitle="Forms ready with content"
          tone="violet"
          icon={<PenSquare className="h-4 w-4" />}
          onClick={() => navigate('/surveys/all')}
        />
        <StatCard
          label="With Responses"
          value={formsWithResponses}
          subtitle="Forms with submitted responses"
          tone="amber"
          icon={<MessageSquare className="h-4 w-4" />}
          onClick={() => navigate('/surveys/all')}
        />
      </MetricCardGrid>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-4">
          <Card className="h-full">
            <CardHeader>
              <div>
                <CardTitle>Quick Access</CardTitle>
                <p className="mt-1 text-xs text-slate-500">
                  Jump into the existing forms workflows without leaving the dashboard.
                </p>
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/surveys/all')}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-primary/40 hover:bg-slate-50"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">Open all forms</p>
                  <p className="mt-1 text-xs text-slate-500">Manage filters, actions, and the full forms table.</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>

              <button
                type="button"
                onClick={() => topResponseForms[0] && navigate(`/surveys/${topResponseForms[0].id}/responses`)}
                disabled={!topResponseForms[0]}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left transition enabled:hover:border-primary/40 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">Review top responses</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Open the form with the highest response volume.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>

              <button
                type="button"
                onClick={() => newestForms[0] && navigate(`/surveys/${newestForms[0].id}/build`)}
                disabled={!newestForms[0]}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left transition enabled:hover:border-primary/40 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">Open latest builder</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Jump into the newest form configuration from the current account set.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-4">
          <Card className="h-full">
            <CardHeader
              action={(
                <Button
                  variant="link"
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                  onClick={() => navigate('/surveys/all')}
                >
                  View All
                </Button>
              )}
            >
              <CardTitle>Recent Forms</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {surveysQuery.isLoading ? (
                <SnapshotEmptyState message="Loading forms..." />
              ) : newestForms.length === 0 ? (
                <SnapshotEmptyState message="No forms have been created yet." />
              ) : (
                newestForms.map((survey) => (
                  <div
                    key={survey.id}
                    className="rounded-lg border border-slate-200 px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">{survey.title}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Created {formatDate(survey.dateCreated ?? survey.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
                      <span>{survey.questionCount ?? 0} questions</span>
                      <span>{survey.responseCount ?? 0} responses</span>
                    </div>
                  </div>
                ))
              )}
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-4">
          <Card className="h-full">
            <CardHeader
              action={(
                <Button
                  variant="link"
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                  onClick={() => navigate('/surveys/all')}
                >
                  Open List
                </Button>
              )}
            >
              <CardTitle>Response Leaders</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {surveysQuery.isLoading ? (
                <SnapshotEmptyState message="Loading response data..." />
              ) : topResponseForms.length === 0 ? (
                <SnapshotEmptyState message="No forms have responses yet." />
              ) : (
                topResponseForms.map((survey) => (
                  <button
                    key={survey.id}
                    type="button"
                    onClick={() => navigate(`/surveys/${survey.id}/responses`)}
                    className="flex w-full items-start justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-primary/40 hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">{survey.title}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {survey.questionCount ?? 0} questions in this form
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold text-slate-900">{survey.responseCount ?? 0}</p>
                      <p className="text-xs text-slate-500">responses</p>
                    </div>
                  </button>
                ))
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader
          action={(
            <Button
              variant="link"
              rightIcon={<ArrowRight className="h-4 w-4" />}
              onClick={() => navigate('/surveys/all')}
            >
              Manage Forms
            </Button>
          )}
        >
          <CardTitle>Needs Attention</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {surveysQuery.isLoading ? (
            <SnapshotEmptyState message="Loading forms that need review..." />
          ) : attentionForms.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              All current forms have questions.
            </div>
          ) : (
            attentionForms.map((survey) => {
              const message = 'Add questions before sending or reviewing this form.';

              return (
                <button
                  key={survey.id}
                  type="button"
                  onClick={() => navigate(`/surveys/${survey.id}/build`)}
                  className="flex w-full items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-primary/40 hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <p className="truncate text-sm font-medium text-slate-900">{survey.title}</p>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{message}</p>
                  </div>
                  <div className="shrink-0 text-right text-xs text-slate-500">
                    <p>{survey.questionCount ?? 0} questions</p>
                    <p>{survey.responseCount ?? 0} responses</p>
                  </div>
                </button>
              );
            })
          )}
        </CardBody>
      </Card>
    </div>
  );
}
